import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import {
    login as authLogin,
    logout as authLogout,
    registerCompany as authRegisterCompany,
    acceptInvite as authAcceptInvite,
} from '@/services/AuthService';

export const useAuthStore = defineStore('auth', () => {
    // State
    const user = ref(null);
    const token = ref(null);
    const isAuthenticated = ref(false);
    const isLoading = ref(false);

    // Getters
    const currentUser = computed(() => user.value);
    const authToken = computed(() => token.value);
    const isLoggedIn = computed(() => isAuthenticated.value);

    // Role checking methods (for UI display only - backend enforces security)
    /** Supports legacy `user.role.name` and multi-role payloads `user.roles` (same idea as backend User::hasRole). */
    const hasRole = (roleName) => {
        if (!user.value || !roleName) return false;
        if (user.value.role?.name === roleName) return true;
        const roles = user.value.roles;
        if (!Array.isArray(roles) || roles.length === 0) return false;
        return roles.some((r) => {
            const n = typeof r === 'string' ? r : r?.name;
            return n === roleName;
        });
    };

    const hasAnyRole = (roleNames) => {
        if (!Array.isArray(roleNames) || roleNames.length === 0) return false;
        return roleNames.some((name) => hasRole(name));
    };

    const isSystemAdmin = computed(() => hasRole('system_admin'));
    const isCompanyAdmin = computed(() => hasRole('company_admin'));
    const isCompanyUser = computed(() => hasRole('company_user'));
    const isFreelanceUser = computed(() => {
        if (!user.value) return false;
        return hasRole('freelance_user') || user.value.company_id === null;
    });

    // Permission checking methods (for UI display only - backend enforces security)
    const hasPermission = (permissionName) => {
        return user.value?.permissions?.includes(permissionName) || false;
    };

    const hasAnyPermission = (permissionNames) => {
        return permissionNames.some(permission => hasPermission(permission));
    };

    // Specific permission checks for common use cases
    const canManageUsers = computed(() => 
        isSystemAdmin.value || 
        hasPermission('manage_company_users') || 
        hasPermission('manage_all_users')
    );

    const canManageAssets = computed(() => 
        isSystemAdmin.value || 
        hasPermission('manage_company_assets') || 
        hasPermission('manage_all_assets') ||
        hasPermission('view_company_assets')
    );

    const canManageProjects = computed(() => 
        isSystemAdmin.value || 
        hasPermission('manage_company_projects') || 
        hasPermission('manage_all_projects') ||
        hasPermission('view_company_projects')
    );

    const canViewAllData = computed(() => 
        isSystemAdmin.value || 
        hasPermission('view_all_data')
    );

    const canManageCompanySettings = computed(() => 
        isSystemAdmin.value || 
        hasPermission('manage_company_settings')
    );

    const canManageSystemSettings = computed(() => 
        isSystemAdmin.value || 
        hasPermission('manage_system_settings')
    );

    const canManageAllCompanies = computed(() => 
        isSystemAdmin.value || 
        hasPermission('manage_all_companies')
    );

    // Worksite information
    const worksiteName = computed(() => user.value?.worksite_name || 'Unknown');
    const workAreaName = computed(() => user.value?.work_area_name || 'Unknown');
    const departmentName = computed(() => user.value?.department_name || 'Unknown');

    // Actions
    const initializeAuth = () => {
        // Check localStorage for existing auth data
        const storedToken = localStorage.getItem('auth_token');
        const storedUser = localStorage.getItem('user');

        if (storedToken && storedUser) {
            try {
                token.value = storedToken;
                user.value = JSON.parse(storedUser);
                isAuthenticated.value = true;
                
                console.log('Auth initialized:', {
                    user: user.value,
                    role: user.value?.role?.name,
                    permissions: user.value?.permissions,
                    worksite: worksiteName.value,
                    workArea: workAreaName.value,
                    department: departmentName.value
                });
            } catch (error) {
                console.error('Error parsing stored user data:', error);
                clearAuth();
            }
        } else {
            // No stored auth data, ensure we're logged out
            clearAuth();
        }
    };

    const checkAuthStatus = () => {
        // Check if localStorage still has valid auth data
        const storedToken = localStorage.getItem('auth_token');
        const storedUser = localStorage.getItem('user');

        if (!storedToken || !storedUser) {
            // Auth data was removed, clear the store
            clearAuth();
            return false;
        }

        // Verify the stored data is still valid
        try {
            const parsedUser = JSON.parse(storedUser);
            if (parsedUser && parsedUser.id && storedToken) {
                // Update store with current localStorage data
                user.value = parsedUser;
                token.value = storedToken;
                isAuthenticated.value = true;
                return true;
            } else {
                clearAuth();
                return false;
            }
        } catch (error) {
            console.error('Error parsing stored user data:', error);
            clearAuth();
            return false;
        }
    };

    const login = async (email, password) => {
        try {
            isLoading.value = true;
            const response = await authLogin(email, password);
            
            if (response && response.user) {
                user.value = response.user;
                token.value = response.token;
                isAuthenticated.value = true;
                
                // Store in localStorage
                localStorage.setItem('auth_token', response.token);
                localStorage.setItem('user', JSON.stringify(response.user));
                
                console.log('Login successful:', {
                    user: response.user,
                    role: response.user?.role?.name,
                    displayName: response.user?.role?.display_name,
                    permissions: response.user?.permissions,
                    worksite: response.user?.worksite_name,
                    workArea: response.user?.work_area_name,
                    department: response.user?.department_name
                });
                
                return response;
            }
        } catch (error) {
            console.error('Login error:', error);
            throw error;
        } finally {
            isLoading.value = false;
        }
    };

    const registerCompany = async (payload) => {
        try {
            isLoading.value = true;
            const response = await authRegisterCompany(payload);

            if (response?.user && response?.token) {
                const u = {
                    ...response.user,
                    username:
                        response.user.username ??
                        response.user.email?.split('@')[0] ??
                        null,
                };
                user.value = u;
                token.value = response.token;
                isAuthenticated.value = true;

                localStorage.setItem('auth_token', response.token);
                localStorage.setItem('user', JSON.stringify(u));
            }

            return response;
        } catch (error) {
            console.error('Register company error:', error);
            throw error;
        } finally {
            isLoading.value = false;
        }
    };

    const acceptInvite = async (payload) => {
        try {
            isLoading.value = true;
            const response = await authAcceptInvite(payload);

            if (response?.user) {
                const u = {
                    ...response.user,
                    username:
                        response.user.username ??
                        response.user.email?.split('@')[0] ??
                        null,
                };
                user.value = u;
                token.value = response.token;
                isAuthenticated.value = true;

                localStorage.setItem('auth_token', response.token);
                localStorage.setItem('user', JSON.stringify(u));
            }

            return response;
        } catch (error) {
            console.error('Accept invite error:', error);
            throw error;
        } finally {
            isLoading.value = false;
        }
    };

    const logout = async () => {
        try {
            if (token.value) {
                await authLogout();
            }
        } catch (error) {
            console.error('Logout error:', error);
            // Even if the API call fails, we should still clear the store
        } finally {
            // Always clear the auth store state
            clearAuth();
        }
    };

    const clearAuth = () => {
        user.value = null;
        token.value = null;
        isAuthenticated.value = false;
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
    };

    const checkAuth = async () => {
        // This could make an API call to verify the token is still valid
        // For now, we'll just check if we have the data
        return isAuthenticated.value && token.value;
    };

    return {
        // State
        user,
        token,
        isAuthenticated,
        isLoading,

        // Getters
        currentUser,
        authToken,
        isLoggedIn,

        // Role checking methods
        hasRole,
        hasAnyRole,
        isSystemAdmin,
        isCompanyAdmin,
        isCompanyUser,
        isFreelanceUser,

        // Permission checking methods
        hasPermission,
        hasAnyPermission,
        canManageUsers,
        canManageAssets,
        canManageProjects,
        canViewAllData,
        canManageCompanySettings,
        canManageSystemSettings,
        canManageAllCompanies,

        // Worksite information
        worksiteName,
        workAreaName,
        departmentName,

        // Actions
        initializeAuth,
        checkAuthStatus,
        login,
        registerCompany,
        acceptInvite,
        logout,
        clearAuth,
        checkAuth
    };
});
